import React, { useContext } from "react";
import { Box, Link, Stack, SvgIcon, Typography } from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { Link as RouterLink } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import {
  CONTACT_EMAIL,
  CONTACT_HOURS,
  CONTACT_PHONE,
  CONTACT_PHONE_HREF,
  CONTACT_WHATSAPP_URL
} from "../../constants/contactInfo";
import { PAGE_ROUTE_BLOG } from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import {
  publicInfoBodySx,
  publicInfoContactCardSx,
  publicInfoContactIconSx,
  publicInfoContactStackSx,
  publicInfoLinkSx
} from "./constants/publicInfoPageStyles";

/** Brand-style WhatsApp glyph (not in @mui/icons-material). */
const WhatsAppIcon = (props: React.ComponentProps<typeof SvgIcon>) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </SvgIcon>
);

type ContactRowProps = {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
};

const ContactRow: React.FC<ContactRowProps> = ({ icon, label, children }) => (
  <Box sx={publicInfoContactCardSx}>
    <Box sx={publicInfoContactIconSx} aria-hidden>
      {icon}
    </Box>
    <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
      <Typography variant="subtitle2" fontWeight={700} color="text.primary">
        {label}
      </Typography>
      <Box sx={{ ...publicInfoBodySx, "& a": publicInfoLinkSx }}>
        {children}
      </Box>
    </Stack>
  </Box>
);

const ContactPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const userGuidePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_USER_GUIDE, language, defaultLang)
      : `/${PAGE_ROUTE_USER_GUIDE}`;
  const learnMorePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_BLOG, language, defaultLang)
      : `/${PAGE_ROUTE_BLOG}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="contact-page"
        eyebrow={t("contact-page-eyebrow", "Your success matters to us")}
        title={t("contact-page-title", "Contact us")}
        intro={t(
          "contact-page-intro",
          "We are here to help you get started, use your account, and answer everyday questions. Reach out using the details below."
        )}
      />

      <PublicInfoCallout testId="contact-page-reassurance">
        {t(
          "contact-page-reassurance",
          "Whether you're evaluating Kivunova FMS or need help with your account, you'll get a thoughtful response from a team that knows the product."
        )}
      </PublicInfoCallout>

      <Stack spacing={2} sx={publicInfoContactStackSx}>
        {CONTACT_EMAIL ? (
          <ContactRow
            icon={<EmailOutlinedIcon color="primary" fontSize="small" />}
            label={t("contact-page-email-label", "Email")}
          >
            <Link
              href={`mailto:${CONTACT_EMAIL}`}
              underline="hover"
              color="primary"
            >
              {CONTACT_EMAIL}
            </Link>
          </ContactRow>
        ) : null}

        {CONTACT_PHONE ? (
          <ContactRow
            icon={<PhoneOutlinedIcon color="primary" fontSize="small" />}
            label={t("contact-page-phone-label", "Phone")}
          >
            <Link
              href={`tel:${CONTACT_PHONE_HREF}`}
              underline="hover"
              color="primary"
            >
              {CONTACT_PHONE}
            </Link>
          </ContactRow>
        ) : null}

        {CONTACT_WHATSAPP_URL ? (
          <ContactRow
            icon={<WhatsAppIcon color="primary" fontSize="small" />}
            label={t("contact-page-whatsapp-label", "WhatsApp")}
          >
            <Link
              href={CONTACT_WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="primary"
            >
              {CONTACT_PHONE || CONTACT_WHATSAPP_URL}
            </Link>
          </ContactRow>
        ) : null}

        {CONTACT_HOURS ? (
          <ContactRow
            icon={<ScheduleOutlinedIcon color="primary" fontSize="small" />}
            label={t("contact-page-hours-label", "Support hours")}
          >
            <Typography component="span" variant="body1" color="text.secondary">
              {CONTACT_HOURS}
            </Typography>
          </ContactRow>
        ) : null}
      </Stack>

      <PublicInfoCtaPanel
        title={t("contact-page-cta-title", "Explore while you wait")}
        testId="contact-page-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t("contact-page-cta-prefix", "Browse the ")}{" "}
          <Link
            component={RouterLink}
            to={learnMorePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("contact-page-cta-learn-more-link", "Learn more")}
          </Link>
          {t("contact-page-cta-mid", " overview or follow the ")}{" "}
          <Link
            component={RouterLink}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("contact-page-cta-guide-link", "User guide")}
          </Link>
          {t("contact-page-cta-suffix", " to get started.")}
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default ContactPage;
